/**
 * Coins and the coin tray.
 *
 * Pre-readers can't read numerals, so each coin is distinguished TWO ways:
 *   1. color — copper (1), silver (2), gold (5)
 *   2. dots  — a dice-style pattern with exactly `value` pips
 * Either cue alone is enough. Styled like little cookie-buttons to match the
 * reference art.
 */
import { COINS, INK, type CoinKind } from '../palette.ts';
import { svgDoc, circle, ellipse, defs, radial, url } from '../primitives.ts';

const R = 40; // coin radius in a 96×96 box centered at (48,48)
const CX = 48;
const CY = 48;

/** Dice-style pip layouts for the values we use (1, 2, 5). */
const PIPS: Record<number, Array<[number, number]>> = {
  1: [[0, 0]],
  2: [
    [-15, -15],
    [15, 15],
  ],
  5: [
    [-16, -16],
    [16, -16],
    [0, 0],
    [-16, 16],
    [16, 16],
  ],
};

export function coin(kind: CoinKind): string {
  const gid = `coin_${kind.value}`;
  const grad = radial(gid, [[0, kind.light], [1, kind.color]], { cx: 0.4, cy: 0.32 });
  const disc = circle(CX, CY, R, { fill: url(gid) });
  const innerRing = circle(CX, CY, R - 8, { fill: 'none', stroke: kind.rim, strokeWidth: 4 });
  const pips = (PIPS[kind.value] ?? PIPS[1])
    .map(([dx, dy]) => circle(CX + dx, CY + dy, 5.5, { fill: INK, stroke: 'none' }))
    .join('');
  const shine = ellipse(CX - 13, CY - 15, 9, 5, { fill: '#ffffff', stroke: 'none', opacity: 0.5 });
  return svgDoc(96, 96, defs(grad) + disc + innerRing + shine + pips);
}

export function coinTextures(): Record<string, { svg: string; w: number; h: number }> {
  const out: Record<string, { svg: string; w: number; h: number }> = {};
  for (const c of COINS) {
    out[`coin_${c.value}`] = { svg: coin(c), w: 96, h: 96 };
  }
  return out;
}
